import { createBrandGraphic } from "@groundspark/brand";
import {
  AuthenticationRequiredError,
  type AppConfig,
  type BrowserAuthManager,
} from "@groundspark/browser-auth";
import { CanvassingAccess } from "./access";
import {
  AuthenticationView,
  AdminView,
  HomeView,
  InboxView,
  MembershipView,
  ProfileView,
} from "./account-views";
import { CanvassApi } from "./api";
import { CanvassView } from "./canvass-view";
import { remaining } from "./domain";
import { ProjectsView, SessionsView } from "./project-views";
import { button, element, message, type View } from "./ui";

type Tab =
  | "home"
  | "membership"
  | "projects"
  | "sessions"
  | "canvass"
  | "admin"
  | "profile"
  | "inbox";
const tabLabels: Readonly<Record<Tab, string>> = {
  home: "Home",
  membership: "Membership",
  projects: "Projects",
  sessions: "Sessions",
  canvass: "Canvassing",
  admin: "Admin",
  profile: "Profile",
  inbox: "Messages",
};

export class CanvassApplication {
  private readonly api: CanvassApi;
  private access: CanvassingAccess | null = null;
  private view: View | null = null;
  private tab: Tab = "home";
  private readonly main = element("main", "app-main");
  private readonly nav = element("nav", "app-tabs");
  private readonly navigationButtons = new Map<Tab, HTMLButtonElement>();
  private navigationTabs = "";
  private readonly bell = button("Messages", "header-button", () => this.show("inbox"));
  private scope = 0;
  private badgeGeneration = 0;
  private readonly cleanup = new Set<Promise<void>>();
  private clock: ReturnType<typeof setInterval> | null = null;
  private badgePoll: ReturnType<typeof setInterval> | null = null;
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void this.access?.refresh();
      void this.refreshBadge();
      this.tick();
    }
  };
  public constructor(
    private readonly root: HTMLElement,
    private readonly auth: BrowserAuthManager,
    config: AppConfig,
  ) {
    this.main.id = "main-content";
    this.main.tabIndex = -1;
    this.nav.setAttribute("aria-label", "Main navigation");
    this.api = new CanvassApi(
      {
        authenticatedFetch: async (path, init) => {
          try {
            return await auth.authenticatedFetch(path, init);
          } catch (failure) {
            this.authenticationFailure(failure);
            throw failure;
          }
        },
      },
      config.apiOrigin,
    );
  }
  public async start(fragment: string): Promise<void> {
    this.clock = setInterval(() => this.tick(), 1000);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    try {
      if (fragment.length > 0) {
        this.authentication(fragment);
        return;
      }
      if (await this.auth.initialize()) await this.enter();
      else this.authentication();
    } catch (failure) {
      this.authentication(undefined, message(failure));
    }
  }
  private authentication(fragment?: string, error?: string): void {
    this.scope += 1;
    this.detachView();
    this.access?.dispose();
    this.access = null;
    if (this.badgePoll !== null) clearInterval(this.badgePoll);
    this.badgePoll = null;
    this.view = new AuthenticationView(
      this.auth,
      this.api,
      () => this.enter(),
      error,
      fragment,
    );
    this.root.replaceChildren(this.view.element);
    this.tick();
  }
  private async enter(): Promise<void> {
    const scope = ++this.scope;
    this.detachView();
    this.access?.dispose();
    const access = new CanvassingAccess(this.api, this.auth, (failure) =>
      this.authenticationFailure(failure),
    );
    this.access = access;
    const logo = element(
      "a",
      "app-header__logo",
      createBrandGraphic(document, { tone: "light" }),
    );
    logo.href = "https://groundspark.app";
    logo.setAttribute("aria-label", "Groundspark home");
    const identity = element(
      "span",
      "app-header__identity",
      this.auth.account!.organization.name,
    );
    const header = element(
      "header",
      "app-header",
      logo,
      identity,
      element(
        "div",
        "header-actions",
        this.bell,
        button("Profile", "header-button", () => this.show("profile")),
      ),
    );
    this.root.replaceChildren(header, this.nav, this.main);
    this.tab = "home";
    this.renderNavigation();
    this.show("home");
    await access.start();
    if (scope !== this.scope) return;
    access.subscribe(() => {
      if (this.access !== access) return;
      if (!access.manager && (this.tab === "projects" || this.tab === "admin"))
        this.show("home");
      this.renderNavigation();
    });
    void this.refreshBadge();
    if (this.badgePoll !== null) clearInterval(this.badgePoll);
    this.badgePoll = setInterval(() => void this.refreshBadge(), 15_000);
  }
  private renderNavigation(): void {
    if (!this.access) return;
    const tabs: Tab[] = [
      "home",
      "membership",
      ...(this.access.manager ? ["projects" as const] : []),
      "sessions",
      "canvass",
      ...(this.access.manager ? ["admin" as const] : []),
    ];
    const signature = tabs.join(",");
    if (signature !== this.navigationTabs) {
      this.navigationTabs = signature;
      this.navigationButtons.clear();
      this.nav.replaceChildren(
        ...tabs.map((tab) => {
          const link = button(tabLabels[tab], "app-tab", () => this.show(tab));
          this.navigationButtons.set(tab, link);
          return link;
        }),
      );
    }
    for (const [tab, link] of this.navigationButtons) {
      if (this.tab === tab) link.setAttribute("aria-current", "page");
      else link.removeAttribute("aria-current");
    }
  }
  private revealNavigation(tab: Tab): void {
    const link = this.navigationButtons.get(tab);
    if (!link) return;
    const container = this.nav.getBoundingClientRect();
    const target = link.getBoundingClientRect();
    if (target.left < container.left) this.nav.scrollLeft += target.left - container.left;
    else if (target.right > container.right)
      this.nav.scrollLeft += target.right - container.right;
  }
  private show(tab: Tab): void {
    const access = this.access;
    if (!access || (!access.manager && (tab === "projects" || tab === "admin"))) return;
    this.detachView();
    this.tab = tab;
    switch (tab) {
      case "home":
        this.view = new HomeView(
          access,
          () => this.show("canvass"),
          () => this.show("sessions"),
        );
        break;
      case "membership":
        this.view = new MembershipView(this.api);
        break;
      case "projects":
        this.view = new ProjectsView(access, () => this.show("canvass"));
        break;
      case "sessions":
        this.view = new SessionsView(access, () => this.show("canvass"));
        break;
      case "canvass":
        this.view = new CanvassView(access);
        break;
      case "admin":
        this.view = new AdminView(this.api);
        break;
      case "profile":
        this.view = new ProfileView(
          this.auth,
          this.api,
          (id) => this.selectOrganization(id),
          () => this.signOut(),
        );
        break;
      case "inbox":
        this.view = new InboxView(this.api, () => void this.refreshBadge());
        break;
    }
    this.main.replaceChildren(this.view.element);
    this.renderNavigation();
    this.revealNavigation(tab);
    this.main.scrollTop = 0;
    this.tick();
  }
  private detachView(): void {
    const view = this.view;
    this.view = null;
    if (!view) return;
    view.element.remove();
    const pending = view.dispose();
    if (pending instanceof Promise) {
      this.cleanup.add(pending);
      void pending.finally(() => this.cleanup.delete(pending));
    }
  }
  private async selectOrganization(id: string): Promise<void> {
    this.scope += 1;
    this.detachView();
    this.access?.dispose();
    this.access = null;
    await Promise.all(this.cleanup);
    try {
      await this.auth.selectOrganization(id);
      await this.enter();
    } catch (failure) {
      this.authentication(undefined, message(failure));
    }
  }
  private async signOut(): Promise<void> {
    this.scope += 1;
    this.detachView();
    this.access?.dispose();
    this.access = null;
    await Promise.all(this.cleanup);
    try {
      await this.auth.signOut();
    } catch {
      /* Local credentials are discarded even when the backend is offline. */
    }
    this.authentication();
  }
  private authenticationFailure(failure: unknown): void {
    if (failure instanceof AuthenticationRequiredError && this.access !== null)
      this.authentication(undefined, "Your sign-in expired. Sign in again.");
  }
  private async refreshBadge(): Promise<void> {
    if (!this.access) return;
    const scope = this.scope;
    const generation = ++this.badgeGeneration;
    try {
      const unread = await this.api.unreadCount();
      if (scope !== this.scope || generation !== this.badgeGeneration) return;
      this.bell.replaceChildren("Messages");
      this.bell.setAttribute(
        "aria-label",
        unread > 0 ? `Messages, ${unread} unread` : "Messages",
      );
      if (unread > 0)
        this.bell.append(element("span", "unread-badge", unread > 99 ? "99+" : String(unread)));
    } catch {
      /* The inbox has its own refresh and reports failures directly. */
    }
  }
  private tick(): void {
    for (const timer of this.root.querySelectorAll<HTMLElement>("[data-expires]"))
      timer.textContent = remaining(timer.dataset.expires!);
    for (const pass of this.root.querySelectorAll<HTMLElement>("[data-expiry]"))
      pass.classList.toggle("qr-pass--expired", Date.parse(pass.dataset.expiry!) <= Date.now());
  }
  public async dispose(): Promise<void> {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.clock !== null) clearInterval(this.clock);
    if (this.badgePoll !== null) clearInterval(this.badgePoll);
    this.detachView();
    this.access?.dispose();
    this.access = null;
    await Promise.all(this.cleanup);
  }
}
